import React, { useCallback } from "react";
import {
  Hits,
  useCurrentRefinements,
  useInstantSearch,
} from "react-instantsearch";
import Hit from "./Hit";
import { translate } from "../../../Services/translation";
import { InjectedContentImage } from "./types/HitProps";
import useIsMobile from "../../../Hooks/useIsMobile";

const allowedCiImageBannerRefinementAttributes = ["categoryPageId"];

const tryGetInjectedContent = (
  results: any,
  isMobile: boolean
): InjectedContentImage[] => {
  const injectedImages = results?.userData?.[0]?.ci_images;
  if (!injectedImages || injectedImages.length === 0) {
    return [];
  }

  const result: InjectedContentImage[] = [];
  injectedImages.forEach((img) => {
    const contentImg = {
      image: img.image,
      url: img.url,
      columns: parseInt(img.columns ?? "1"),
      position: parseInt(img.position ?? "0"),
      mobile: img.mobile == "true",
      desktop: img.desktop == "true",
    } as InjectedContentImage;

    const include =
      (isMobile && contentImg.mobile) || (!isMobile && contentImg.desktop);
    if (include) {
      result.push(contentImg);
    }
  });
  return result;
};

const AlgoliaResults = () => {
  const { results } = useInstantSearch();
  const { items: currentRefinementItems } = useCurrentRefinements();
  const isMobile = useIsMobile();

  const shouldSuppressInjectedContent = currentRefinementItems.some((refinement) => {
    const hasRefinements = Array.isArray(refinement.refinements) && refinement.refinements.length > 0;
    return hasRefinements && !allowedCiImageBannerRefinementAttributes.includes(refinement.attribute);
  });

  const transformItems = useCallback(
    (items, { results }) => {
      if (shouldSuppressInjectedContent) {
        return items;
      }

      const injectedContent = tryGetInjectedContent(results, isMobile);

      if (injectedContent.length === 0) {
        return items;
      }
      
      // Custom content is provided with the result from Algolia, inject it at specified positions.

      // Sort injected content by position to avoid index shifting issues
      injectedContent.sort((a, b) => a.position - b.position);

      // Inject content into into correct positions
      let modifiedItems = [...items];
      injectedContent.forEach((ic) => {
        const index = Math.max(0, ic.position - 1);
        modifiedItems.splice(index, 0, {
          objectID: "injected-content-" + ic.position,
          __injectedContent: ic,
        });
      });
      return modifiedItems;
    },
    [isMobile, shouldSuppressInjectedContent]
  );

  if (!results.__isArtificial && results.nbHits === 0) {
    return (
      <div className="algolia-search-results__mobile-padding">
        {results.query ? (
          <>
            {translate("algolia.results.no-results.title")}{" "}
            <strong>{results.query}</strong>
          </>
        ) : (
          <>{translate("algolia.results.no-results.title-no-query")}</>
        )}
      </div>
    );
  }

  return <Hits transformItems={transformItems} hitComponent={Hit} />;
};

export default AlgoliaResults;
